import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ethers } from 'ethers';
import HiveSelector from '../combComponents/HiveSelector';
import BeeLeveler from '../combComponents/BeeLeveler';
import { useHoneyFarm } from '../../context/HoneyFarmContext';
import useWindowSize from '../../customHooks/useWindowSize';

interface HoneyCombsProps {
  signer?: ethers.Signer;
  address?: string;
}

const HoneyCombs: React.FC<HoneyCombsProps> = (props) => {
  const [txPending, setTxPending] = useState(false);
  const [activeComponent, setActiveComponent] = useState<boolean>(true); // true = level up (BeeLeveler), false = claim (HiveSelector)
  
  const { beeHoney, hives, bees, loadResource, loaded, isLoading } = useHoneyFarm();
  const windowSize = useWindowSize();
  const isDesktop = windowSize[0] >= 1024; // useWindowSize returns [width, height]

  // Ensure hives and bees are loaded when visiting this page directly
  useEffect(() => {
    if (!loaded.hives) {
      loadResource('hives').catch((error) => console.error('Failed to load hives:', error));
    }
    if (!loaded.bees) {
      loadResource('bees').catch((error) => console.error('Failed to load bees:', error));
    }
  }, [loadResource, loaded.hives, loaded.bees]);

  // Check if we're still loading required resources
  const isLoadingResources = isLoading.hives || isLoading.bees;

  // Memoize token ID calculations to avoid recalculation on every render
  const addressHiveTokenIds = useMemo(() => 
    hives.map((hive: any) => hive.tokenId || hive.id).filter(Boolean),
    [hives]
  );
  
  const addressBeeTokenIds = useMemo(() => 
    bees.map((bee: any) => bee.id || bee.tokenId).filter(Boolean),
    [bees]
  );

  // Memoize the setTxPending callback to prevent unnecessary re-renders
  const handleSetTxPending = useCallback((pending: boolean) => {
    setTxPending(pending);
  }, []);

  useEffect(() => {
    // This effect could be used to trigger refreshes when txPending changes
    // For now, we'll leave it as a placeholder
  }, [txPending]);

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header */}
      <div className="w-full p-4 bg-black bg-opacity-60 text-white shadow-md rounded-md mb-6 border border-[#c4f6f9]">
        <h1 className="text-xl font-bold text-[#c4f6f9]">Honeycombs</h1>
        <div className="text-sm text-gray-300 mt-2">
          Level up your bees with honeycombs or extract honeycombs from your hives.
        </div>
      </div>

      {/* Toggle Switch */}
      <div className="mb-6 p-4 bg-black bg-opacity-60 rounded-md border border-[#55bac3]">
        <div className="flex items-center justify-center gap-6">
          <button
            onClick={() => setActiveComponent(false)}
            className={`px-6 py-3 rounded-md font-bold text-lg transition-colors ${
              !activeComponent
                ? 'bg-[#ffa2f8] text-black'
                : 'bg-transparent border-2 border-[#55bac3] text-[#55bac3] hover:border-[#ffa2f8] hover:text-[#ffa2f8]'
            }`}
          >
            EXTRACT COMBS
          </button>
          <button
            onClick={() => setActiveComponent(true)}
            className={`px-6 py-3 rounded-md font-bold text-lg transition-colors ${
              activeComponent
                ? 'bg-[#ffa2f8] text-black'
                : 'bg-transparent border-2 border-[#55bac3] text-[#55bac3] hover:border-[#ffa2f8] hover:text-[#ffa2f8]'
            }`}
          >
            LEVEL UP BEES
          </button>
        </div>
      </div>

      {/* Loading Indicator */}
      {txPending && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center">
          <div className="bg-black bg-opacity-90 border-2 border-[#ffa2f8] p-8 rounded-lg text-white text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ffa2f8] mx-auto mb-4"></div>
            <div className="text-[#ffa2f8]">Processing transaction...</div>
          </div>
        </div>
      )}

      {/* Loading Resources */}
      {isLoadingResources ? (
        <div className="p-8 text-center bg-black bg-opacity-60 rounded-md border border-[#55bac3]">
          <div className="animate-pulse text-[#c4f6f9]">Loading assets...</div>
        </div>
      ) : (
        <>
          {/* Component Switcher */}
          {activeComponent === false && (
            <HiveSelector 
              hiveIds={addressHiveTokenIds}
              txPending={txPending}
              setTxPending={handleSetTxPending}
              isDesktop={isDesktop}
              addressHoney={beeHoney || 0}
              address={props.address}
              addressHiveTokenIds={addressHiveTokenIds}
            />
          )}

          {activeComponent === true && (
            <BeeLeveler
              hiveIds={addressHiveTokenIds}
              txPending={txPending}
              setTxPending={handleSetTxPending}
              isDesktop={isDesktop}
              addressBeeTokenIds={addressBeeTokenIds}
              address={props.address}
            />
          )}
        </>
      )}
    </div>
  );
};

export default HoneyCombs;
